import CryptoJS from 'crypto-js';
import { encryptToUrl, decryptFromUrl } from '../utils/crypto';

const STORAGE_KEY = 'kaarya_user';
const IMPERSONATOR_KEY = 'kaarya_impersonator';
const REMEMBER_KEY = 'kaarya_remember';
const SESSION_EXPIRED_KEY = 'kaarya_session_expired';
const SECRET_KEY = 'kaaryaalaya_v2_client_key';

const fromUrlSafe = (str) => str.replace(/-/g, '+').replace(/_/g, '/');
const toUrlSafe = (base64) => base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');

const decryptRaw = (raw) => {
  try {
    if (!raw || typeof raw !== 'string') return null;
    const parts = raw.split('://');
    const cipherUrlSafe = parts.length === 2 ? parts[1] : raw;
    const cipher = fromUrlSafe(cipherUrlSafe);
    const bytes = CryptoJS.AES.decrypt(cipher, SECRET_KEY);
    const json = bytes.toString(CryptoJS.enc.Utf8);
    if (!json) return null;
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
};

const encryptForStorage = (obj) => {
  const json = JSON.stringify(obj);
  const cipher = CryptoJS.AES.encrypt(json, SECRET_KEY).toString();
  return `kaarya://${toUrlSafe(cipher)}`;
};

const decryptStored = () => {
  try {
    return decryptRaw(window.localStorage.getItem(STORAGE_KEY));
  } catch (_) {
    return null;
  }
};

const AuthService = {
  getApiKey() {
    const data = decryptStored();
    return data?.apiKey || null;
  },
  getUser() {
    return decryptStored();
  },
  clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.localStorage.removeItem(IMPERSONATOR_KEY);
    } catch (_) {}
  },

  // --- Impersonation ("login as") support ---
  isImpersonating() {
    try {
      return !!window.localStorage.getItem(IMPERSONATOR_KEY);
    } catch (_) {
      return false;
    }
  },
  getImpersonator() {
    try {
      return decryptRaw(window.localStorage.getItem(IMPERSONATOR_KEY));
    } catch (_) {
      return null;
    }
  },
  // Swaps the active session for the target staff member's session, stashing
  // the current (real) session so it can be restored later.
  startImpersonation(payload) {
    try {
      if (!window.localStorage.getItem(IMPERSONATOR_KEY)) {
        const current = window.localStorage.getItem(STORAGE_KEY);
        if (current) window.localStorage.setItem(IMPERSONATOR_KEY, current);
      }
      window.localStorage.setItem(STORAGE_KEY, encryptForStorage(payload));
    } catch (_) {}
  },
  // Restores the original session that was active before impersonation started.
  stopImpersonation() {
    try {
      const original = window.localStorage.getItem(IMPERSONATOR_KEY);
      if (original) {
        window.localStorage.setItem(STORAGE_KEY, original);
        window.localStorage.removeItem(IMPERSONATOR_KEY);
      }
    } catch (_) {}
  },

  // "Remember me" on the login form -- independent of the signed-in session
  // above, so it survives Logout (the whole point is to still have the
  // fields pre-filled the next time someone signs back in on this device).
  rememberCredentials(username, password) {
    try {
      window.localStorage.setItem(REMEMBER_KEY, encryptToUrl({ username, password }));
    } catch (_) {}
  },
  getRememberedCredentials() {
    try {
      return decryptFromUrl(window.localStorage.getItem(REMEMBER_KEY));
    } catch (_) {
      return null;
    }
  },
  forgetCredentials() {
    try {
      window.localStorage.removeItem(REMEMBER_KEY);
    } catch (_) {}
  },

  // Set right before a forced redirect to /login so that page can greet the
  // user with a proper explanation instead of dropping them back at a blank
  // sign-in form. sessionStorage (not localStorage) so it can only ever
  // apply to the redirect that set it, never linger into a later visit.
  flagSessionExpired() {
    try {
      window.sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
    } catch (_) {}
  },
  // Reads and immediately clears the flag, so it only ever fires once.
  consumeSessionExpiredFlag() {
    try {
      const flagged = window.sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
      window.sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      return flagged;
    } catch (_) {
      return false;
    }
  },
};

export default AuthService;
